// ─── Primary Insight Transformer ─────────────────────────────────────────────
//
// Selects "the one thing" to show at the top of the dashboard as the hero
// card. The selection is deterministic and explainable:
//
//   Priority waterfall:
//     1. Critical exception from exceptionDetector (highest urgency)
//     2. Top-priority insight from insights engine with direction=negative
//     3. Worst-scoring pillar (current score)
//     4. Top-priority insight with direction=positive
//     5. Best-scoring pillar (when everything is fine)
//
// Each branch produces a PrimaryInsight with lens-aware copy, up to 3
// contributing signals, a suggested next step, and a rationale.
//
// Pure function. No React, no API calls. Unit tested.
// ──────────────────────────────────────────────────────────────────────────────

import type { Exception } from '../exceptionDetector';
import type { Insight } from '../insights';
import type { PillarResult } from '../pillarScoring';
import type {
  PrimaryInsight,
  ContributingSignal,
  SeverityBand,
  ConfidenceLevel,
  TrustIndicators,
  TrendDirection,
} from './types';
import {
  lensCopy,
  primaryInsightHeadline,
  nextStepForDomain,
  buildPatientExplanation,
  buildClinicianExplanation,
  sanitizePatientText,
} from './lensCopy';

// ─── Inputs ──────────────────────────────────────────────────────────────────

export interface PrimaryInsightInput {
  exceptions: Exception[];
  insights: Insight[];
  pillarResults: Record<string, PillarResult>;
  pillarDeltas?: Record<string, number>;
  trust: TrustIndicators;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Map pillar score (0-100) to severity band. */
function scoreToSeverity(score: number | null): SeverityBand {
  if (score == null) return 'no_data';
  if (score >= 80) return 'excellent';
  if (score >= 60) return 'good';
  if (score >= 40) return 'fair';
  return 'attention';
}

/** Classify a pillar delta the same way computeSystemMap does. */
function classifyDelta(delta: number | null | undefined): TrendDirection {
  if (delta == null) return 'unknown';
  if (delta > 3) return 'improving';
  if (delta < -3) return 'declining';
  return 'stable';
}

/** Pick up to 3 contributing signals from a pillar's inputs, ranked by weight. */
function contributorsFromPillar(pillar: PillarResult, domain: string): ContributingSignal[] {
  return [...pillar.inputs]
    .sort((a, b) => b.weight - a.weight)
    .slice(0, 3)
    .map((input, i) => ({
      id: `${domain}:${input.label}:${i}`,
      label: input.label,
      value: input.rawValue,
      direction: 'unknown',
      domain,
    }));
}

/** Pick up to 3 contributing insights for a given negative/positive context. */
function contributorsFromInsights(
  insights: Insight[],
  direction: 'positive' | 'negative',
): ContributingSignal[] {
  return insights
    .filter((i) => i.direction === direction)
    .slice(0, 3)
    .map((i) => ({
      id: `insight:${i.id}`,
      label: i.title,
      value: i.value,
      direction: direction === 'positive' ? 'improving' : 'declining',
      domain: i.category,
    }));
}

/** Find the pillar with the lowest non-null score. */
function worstPillar(
  pillars: Record<string, PillarResult>,
): { key: string; result: PillarResult } | null {
  let worst: { key: string; result: PillarResult } | null = null;
  for (const [key, result] of Object.entries(pillars)) {
    if (result.score == null) continue;
    if (worst == null || result.score < worst.result.score!) {
      worst = { key, result };
    }
  }
  return worst;
}

/** Find the pillar with the highest non-null score. */
function bestPillar(
  pillars: Record<string, PillarResult>,
): { key: string; result: PillarResult } | null {
  let best: { key: string; result: PillarResult } | null = null;
  for (const [key, result] of Object.entries(pillars)) {
    if (result.score == null) continue;
    if (best == null || result.score > best.result.score!) {
      best = { key, result };
    }
  }
  return best;
}

// ─── Defense-in-depth sanitizer ──────────────────────────────────────────────
//
// Final-pass guarantor that every user-visible patient-side string is free
// of clinical notation. If any branch above accidentally leaks a raw clinical
// string into the patient side of a LensCopy, this pass catches it at
// runtime and substitutes a safe plain-language fallback.

function sanitizeFields(
  insight: PrimaryInsight,
): PrimaryInsight {
  const fallbackCtx = {
    domain: insight.domain,
    severity: insight.severity,
  };
  const scrub = (patient: string) => sanitizePatientText(patient, fallbackCtx);

  return {
    ...insight,
    headline: {
      patient: scrub(insight.headline.patient),
      clinician: insight.headline.clinician,
    },
    explanation: {
      patient: scrub(insight.explanation.patient),
      clinician: insight.explanation.clinician,
    },
    nextStep: {
      patient: scrub(insight.nextStep.patient),
      clinician: insight.nextStep.clinician,
    },
    rationale: insight.rationale
      ? {
          patient: scrub(insight.rationale.patient),
          clinician: insight.rationale.clinician,
        }
      : undefined,
  };
}

// ─── Main transformer ────────────────────────────────────────────────────────

/**
 * Select the primary insight to show at the top of the dashboard.
 * Returns null only when there is literally no scored pillar and no data.
 */
export function computePrimaryInsight(input: PrimaryInsightInput): PrimaryInsight | null {
  const { exceptions, insights, pillarResults, pillarDeltas = {}, trust } = input;

  // Branch 1: Critical exception takes precedence
  const critical = exceptions.find((e) => e.category === 'critical');
  if (critical) {
    const pillar = pillarResults[critical.pillar];
    const domain = critical.pillar;
    const severity: SeverityBand = 'attention';
    const trendDir: TrendDirection =
      critical.changeDirection === 'decreased' ? 'declining'
      : critical.changeDirection === 'increased' ? 'declining'
      : 'unknown';
    // Detect the "sustained" case so the clinician builder gets a precise
    // duration phrase instead of a generic "multiple weeks".
    const isSustained = /sustained|consecutive/i.test(critical.metric) || /consecutive/i.test(critical.summary);
    const durationPhrase = isSustained ? 'three consecutive weekly check-ins' : undefined;
    return sanitizeFields({
      id: `exception:${critical.pillar}:${critical.metric}`,
      domain,
      headline: primaryInsightHeadline(domain, severity),
      explanation: lensCopy(
        buildPatientExplanation({ domain, severity, trend: trendDir }),
        buildClinicianExplanation({
          domain,
          severity,
          trend: trendDir,
          metric: critical.metric,
          currentValue: critical.currentValue,
          durationPhrase,
        }),
      ),
      contributors: pillar ? contributorsFromPillar(pillar, domain) : [],
      nextStep: nextStepForDomain(domain, severity),
      severity,
      confidence: trust.confidence,
      rationale: lensCopy(
        'We surfaced this because your recent data shows a meaningful change.',
        `Driver: ${critical.metric}. Category: ${critical.category}.`,
      ),
    });
  }

  // Branch 2: Top negative insight
  const topNegative = insights.find((i) => i.direction === 'negative');
  if (topNegative) {
    const domain = topNegative.category;
    const relatedPillar = pillarResults[domain] ?? null;
    const severity: SeverityBand = relatedPillar
      ? scoreToSeverity(relatedPillar.score)
      : 'fair';
    return sanitizeFields({
      id: `insight:${topNegative.id}`,
      domain,
      // Insight titles are already short and plain — safe for both lenses.
      headline: lensCopy(topNegative.title, topNegative.title),
      explanation: lensCopy(
        buildPatientExplanation({ domain, severity, trend: 'declining' }),
        buildClinicianExplanation({
          domain,
          severity,
          trend: 'declining',
          metric: topNegative.title,
        }),
      ),
      contributors: contributorsFromInsights(insights, 'negative'),
      nextStep: nextStepForDomain(domain, severity),
      severity,
      confidence: trust.confidence,
      rationale: lensCopy(
        'This is the biggest change in your recent data.',
        `Top-ranked negative insight; priority ${topNegative.priority}.`,
      ),
    });
  }

  // Branch 3: Worst scoring pillar
  const worst = worstPillar(pillarResults);
  if (worst && worst.result.score! < 60) {
    const domain = worst.key;
    const severity = scoreToSeverity(worst.result.score);
    const delta = pillarDeltas[domain];
    const trend = classifyDelta(delta);
    return sanitizeFields({
      id: `pillar:${domain}`,
      domain,
      headline: primaryInsightHeadline(domain, severity),
      explanation: lensCopy(
        buildPatientExplanation({ domain, severity, trend }),
        buildClinicianExplanation({
          domain,
          severity,
          trend,
          currentValue: worst.result.score,
        }),
      ),
      contributors: contributorsFromPillar(worst.result, domain),
      nextStep: nextStepForDomain(domain, severity),
      severity,
      confidence: trust.confidence,
      rationale: lensCopy(
        'We surfaced this because it has the lowest score in your system map right now.',
        `Lowest-scoring pillar this period: ${domain}.`,
      ),
    });
  }

  // Branch 4: Top positive insight
  const topPositive = insights.find((i) => i.direction === 'positive');
  if (topPositive) {
    const domain = topPositive.category;
    const severity: SeverityBand = 'good';
    return sanitizeFields({
      id: `insight:${topPositive.id}`,
      domain,
      // Insight titles are already short and plain — safe for both lenses.
      headline: lensCopy(topPositive.title, topPositive.title),
      explanation: lensCopy(
        buildPatientExplanation({ domain, severity, trend: 'improving' }),
        buildClinicianExplanation({
          domain,
          severity,
          trend: 'improving',
          metric: topPositive.title,
        }),
      ),
      contributors: contributorsFromInsights(insights, 'positive'),
      nextStep: nextStepForDomain(domain, severity),
      severity,
      confidence: trust.confidence,
      rationale: lensCopy(
        'We surfaced this because you are trending in a supportive direction.',
        `Top-ranked positive insight; priority ${topPositive.priority}.`,
      ),
    });
  }

  // Branch 5: Best pillar (all clear)
  const best = bestPillar(pillarResults);
  if (best) {
    const domain = best.key;
    const severity = scoreToSeverity(best.result.score);
    return sanitizeFields({
      id: `pillar-best:${domain}`,
      domain,
      headline: primaryInsightHeadline(domain, severity),
      explanation: lensCopy(
        buildPatientExplanation({ domain, severity, trend: 'stable' }),
        buildClinicianExplanation({
          domain,
          severity,
          trend: 'stable',
          currentValue: best.result.score,
        }),
      ),
      contributors: contributorsFromPillar(best.result, domain),
      nextStep: nextStepForDomain(domain, severity),
      severity,
      confidence: trust.confidence,
      rationale: lensCopy(
        'Things look stable right now, so we are highlighting what is working.',
        'No flagged signals this period; surfacing the top-performing pillar.',
      ),
    });
  }

  // No data at all
  return null;
}
